"use client";

import { useEffect, useState } from "react";
import { ArrowDown, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

// GEM model — cloud cover + precip probability, and fallback values
const HEADER_OM_URL = "https://api.open-meteo.com/v1/forecast?latitude=50.267&longitude=-119.272&current=temperature_2m,relative_humidity_2m,wind_speed_10m,cloud_cover&hourly=precipitation_probability&models=gem_seamless&elevation=482&timezone=America%2FVancouver&forecast_days=1&temperature_unit=celsius";

// Environment Canada Vernon Auto station (CWJV) — latest real observation
const HEADER_EC_URL = "https://api.weather.gc.ca/collections/swob-realtime/items?url=CWJV&sortby=-date_tm-value&limit=1&f=json";

// SWOB property names vary slightly by station/report — try candidates in order
function pickHeaderObs(props, keys) {
  for (const k of keys) {
    const v = parseFloat(props?.[k]);
    if (Number.isFinite(v)) return v;
  }
  return null;
}
function useVernonWeather() {
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    async function fetchWeather() {
      try {
        // Fetch model + station in parallel; station failure is non-fatal
        const [om, ec] = await Promise.all([fetch(HEADER_OM_URL).then(r => r.json()), fetch(HEADER_EC_URL).then(r => r.json()).catch(() => null)]);

        // Timezone-safe precip lookup: match against the API's own current
        // timestamp (America/Vancouver), not the visitor's browser clock.
        const hourKey = (om.current.time ?? "").slice(0, 13);
        const hourIdx = om.hourly.time.findIndex(t => t.startsWith(hourKey));
        const precipProb = hourIdx >= 0 ? om.hourly.precipitation_probability[hourIdx] ?? 0 : 0;

        // Base values from GEM model
        const next = {
          temp: Math.round(om.current.temperature_2m),
          humidity: om.current.relative_humidity_2m,
          windspeed: Math.round(om.current.wind_speed_10m),
          cloud_cover: om.current.cloud_cover,
          // model only — no sky sensor at CWJV
          precip_prob: precipProb
        };

        // Overlay real Environment Canada observations where present
        const props = ec?.features?.[0]?.properties;
        if (props) {
          const t = pickHeaderObs(props, ["air_temp"]);
          const rh = pickHeaderObs(props, ["rel_hum"]);
          const ws = pickHeaderObs(props, ["avg_wnd_spd_10m_pst10mts", "avg_wnd_spd_10m_pst2mts", "avg_wnd_spd_10m_pst1hr"]);
          if (t != null) next.temp = Math.round(t);
          if (rh != null) next.humidity = Math.round(rh);
          if (ws != null) next.windspeed = Math.round(ws);
        }
        next.condition = next.cloud_cover < 20 ? "CLEAR" : next.cloud_cover < 50 ? "PARTLY CLOUDY" : next.cloud_cover < 80 ? "MOSTLY CLOUDY" : "OVERCAST";
        setWeather(next);
      } catch {
        setError(true);
      }
    }
    fetchWeather();
    const interval = setInterval(fetchWeather, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  return {
    weather,
    error
  };
}
function SiteWeather() {
  const {
    weather,
    error
  } = useVernonWeather();
  if (error) return null;
  const imagingGood = weather && weather.cloud_cover < 30 && weather.precip_prob < 20;
  return <div className="absolute top-6 right-6 z-20 flex flex-col items-end gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/70 md:top-10 md:right-10 md:text-[11px]">
      {!weather ? <span className="text-foreground/30 animate-pulse">FETCHING WX...</span> : <>
          <div className="flex items-center gap-2">
            <span className="size-1.5 animate-pulse" style={{
          backgroundColor: imagingGood ? "#4ade80" : "#f87171"
        }} />
            <span className="text-foreground/40">VERNON BC</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-secondary">{weather.condition}</span>
            <span className="text-foreground/40">·</span>
            <span className="text-foreground/80 tabular-nums">{weather.temp}°C</span>
          </div>
          <div className="flex items-center gap-2 text-foreground/50">
            <span>WIND {weather.windspeed} KM/H</span>
            <span className="text-secondary">·</span>
            <span>RH {weather.humidity}%</span>
            <span className="text-secondary">·</span>
            <span>PRECIP {weather.precip_prob}%</span>
          </div>
          <div className="text-[9px] tracking-widest" style={{
        color: imagingGood ? "#4ade80" : "#f87171"
      }}>
            {imagingGood ? "▲ IMAGING CONDITIONS NOMINAL" : "▼ IMAGING CONDITIONS POOR"}
          </div>
        </>}
    </div>;
}
export default function TheAstralThreshold() {
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-astral-threshold" className="text-foreground relative w-full h-auto min-h-screen lg:h-screen lg:min-h-[800px] overflow-hidden dark !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697763/img/solar_first_light.webp)] !bg-[url(https://solobservatory.com/wp-content/uploads/2026/06/First-Light-Final.jpg)]" style={{
    backgroundImage: 'url(https://solobservatory.com/wp-content/uploads/2026/06/andromeda-scaled.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }}>
      <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse at center, transparent 30%, hsla(240, 10%, 4%, 0.7) 100%)"
    }} />
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "linear-gradient(to bottom, hsla(240, 10%, 4%, 0.2) 0%, hsla(240, 10%, 4%, 0.5) 50%, hsla(240, 10%, 4%, 0.8) 100%)"
    }} />
      <div className="absolute top-6 left-6 md:top-10 md:left-10 z-20 hidden sm:flex items-center gap-3 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-foreground/70">
        <Film className="size-3.5 text-secondary" />
        <span>COORD: 50° 16' 01" N / 119° 16' 19" W</span>
        <span className="text-secondary">·</span>
        <span>ASTROPHOTOGRAPHY</span>
        <span className="hidden sm:inline text-secondary">·</span>
        <span className="hidden sm:inline">CITIZEN SCIENCE</span>
      </div>
      <SiteWeather />
      <div className="relative z-10 w-full h-full min-h-screen lg:h-screen lg:min-h-[800px] flex flex-col justify-center lg:items-end px-6 md:px-10 lg:px-16 pt-32 pb-24 md:pb-32">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-4 items-center lg:items-end">
          <div ref={headlineRef} className={`lg:col-span-7 transition-all duration-1000 ease-out overflow-hidden ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <h1 className="font-default font-bold uppercase leading-[0.85] text-foreground" style={{
            fontSize: 'clamp(3rem, 9vw, 9rem)',
            letterSpacing: '-0.05em'
          }}>
              <span className="block ![padding:5px_0px_18px_0px]">COSMOS,</span>
              <span className="block text-transparent !font-normal !text-right" style={{
              WebkitTextStroke: '1px rgb(197, 89, 43)'
            }}>OBSERVED.</span>
            </h1>
          </div>
          <div ref={cardRef} className={`lg:col-span-5 lg:max-w-[480px] lg:ml-auto w-full transition-all duration-1000 delay-300 ease-out self-center lg:self-end ${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="border border-secondary/30 shadow-2xl rounded-md !p-[40px]" style={{
            backgroundColor: 'rgba(15, 17, 21, 0.6)',
            backdropFilter: 'blur(24px)'
          }}>
              <div className="flex items-center gap-3 mb-6">
                <span className="size-1.5 bg-secondary animate-pulse" />
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-secondary">01 — LIVE OBSERVATION</span>
              </div>
              <p className="font-sans text-lg md:text-[18px] leading-[1.5] text-foreground/90 mb-8">Current Focus: First Light with Daystar Quark. High-frame-rate H-alpha imaging capturing plasma filaments and surface prominences in real-time.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-foreground text-background hover:bg-accent-foreground font-default font-semibold uppercase tracking-[0.15em] text-xs h-12 px-6 flex-1 shadow-md rounded-md">
                  <Link to="/ObservatoryPage">LIVESTREAM offline</Link>
                </Button>
                <Button asChild variant="outline" className="border shadow-xs dark:bg-input/30 dark:border-input dark:hover:bg-input/50 border-foreground/30 bg-transparent text-foreground hover:bg-foreground/10 hover:text-foreground font-default font-semibold uppercase tracking-[0.15em] text-xs h-12 px-6 flex-1 rounded-md">
                  <Link to="/TheLogbookPage">MISSION LOGS</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-6 md:left-10 lg:left-16 z-20 flex items-center gap-4">
        <div className="h-px w-24 md:w-40 bg-secondary" />
        <div className="flex items-center gap-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-secondary animate-pulse">
          <span>DESCEND</span>
          <ArrowDown className="size-3" />
        </div>
      </div>
    </section>;
}